import { axiosInstance } from "./networking";

/**
 * Support tickets, filed in IC_Ticket (project TD) through Thrive's server
 * (`thrive_server/apps/ic_ticket/`). The browser only ever talks to Thrive;
 * Thrive's server calls IC_Ticket as the logged-in user. People are names
 * only — IC_Ticket e-mail addresses and user ids never reach the browser.
 */

/** IC_Ticket's stored Issue statuses. */
export type TicketStatus =
  | "NEW"
  | "TRIAGED"
  | "IN_PROGRESS"
  | "WAITING"
  | "FIXED"
  | "RETEST"
  | "CLOSED"
  | "REJECTED";

export type TicketPriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

/** Set only while status is WAITING. CLARIFICATION = waiting on the requester. */
export type TicketWaitingReason = "CLARIFICATION" | "APPROVAL" | "REVIEW" | null;

export interface TicketSummary {
  id: string;
  displayId: string;
  title: string;
  status: TicketStatus;
  waitingReason: TicketWaitingReason;
  /** status WAITING + waitingReason CLARIFICATION: the team asked a question. */
  needsReply: boolean;
  priority: TicketPriority;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  assignedTo: string | null;
  commentCount: number;
}

export interface TicketComment {
  id: string;
  content: string;
  createdAt: string;
  author: string;
}

export interface TicketDetail extends TicketSummary {
  description: string;
  comments: TicketComment[];
}

export interface TicketPage {
  items: TicketSummary[];
  nextCursor: string | null;
}

export interface NewTicketInput {
  title: string;
  description: string;
  priority?: TicketPriority;
}

/** "project" = sees every Thrive ticket (managers); "own" = only their own. */
export type TicketAccess = "own" | "project";

export const TICKET_PRIORITIES: TicketPriority[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

const ticketPath = (id: string) => `/ic-ticket/tickets/${encodeURIComponent(id)}`;

export const getTickets = (
  opts: { status?: string; cursor?: string | null; q?: string } = {},
): Promise<TicketPage> =>
  axiosInstance
    .get("/ic-ticket/tickets", {
      params: {
        ...(opts.status ? { status: opts.status } : {}),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
        ...(opts.q ? { q: opts.q } : {}),
      },
    })
    .then((r) => r.data);

export const getTicket = (id: string): Promise<TicketDetail> =>
  axiosInstance.get(ticketPath(id)).then((r) => r.data);

export const createTicket = (body: NewTicketInput): Promise<TicketDetail> =>
  axiosInstance.post("/ic-ticket/tickets", body).then((r) => r.data);

export const addTicketComment = (id: string, content: string): Promise<TicketComment> =>
  axiosInstance.post(`${ticketPath(id)}/comments`, { content }).then((r) => r.data);

export const getTicketAccess = (): Promise<TicketAccess> =>
  axiosInstance
    .get("/ic-ticket/me")
    .then((r) => (r.data?.itemAccess === "project" ? "project" : "own"));

/** HTTP status of a failed ticket call (undefined for network errors). */
export const ticketErrorStatus = (err: any): number | undefined =>
  err?.response?.status;

/** A message to show for a failed ticket call. */
export const ticketErrorMessage = (err: any): string => {
  const status = ticketErrorStatus(err);
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status === 404) return "Ticket not found.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if ((status === 400 || status === 403) && err?.response?.data?.message) {
    return err.response.data.message;
  }
  if (status === 502 || status === undefined) {
    return "The ticket service is unavailable right now. Please try again later.";
  }
  return "Something went wrong. Please try again.";
};
